// Iter-158.13.2.20e.11 — Workspace destination helper.
//
// Pure function returning the destination-first vocabulary for a
// `/beta/me/workspaces` row so `MyProjects` can present:
//
//   "<Project name>"                   (primary line)
//   "<Production name> · <destination>"
//     (subtitle — Production echo dropped when it matches the
//      project name so short/wizard-created names don't shout)
//   "<permission>"                     (tertiary line — discreet role)
//
// Iter-158.13.2.20e.15 — the earlier "Walkabout" vocabulary token
// was retired; it visually occupied the slot where users expect a
// Production label and caused an identity-muddle audit finding.
// The subtitle is now Production + destination only.
//
// Roles (owner / admin / contributor / viewer / producer /
// script_supervisor) are PERMISSIONS, not destinations, and are
// intentionally demoted from the loud uppercase tail they used to
// occupy pre-.20e.11.
//
// Destination resolution:
//   * Hub row (`is_hub === true`) → "Production"
//   * Production-scope working row (`is_hub=false && dept_key === null`)
//     — the .20e.11 additive row for Contributor / Viewer / Producer
//     / Script Supervisor without Department membership → "Production"
//   * Department row (`dept_key === "hair_makeup" | "art" | ...`) →
//     the row's own server-provided `dept_label` verbatim (already
//     "Hair & Makeup", "Art", "Costume", "VFX & AI").
//
// Permission resolution is CONTEXT-AWARE: an "admin" on a Hub row
// is a Production Admin, but an "admin" on a Department row is a
// Department Admin — those are architecturally different scopes.
//
// `landing_url` is NOT consulted — the workspace row's
// server-authoritative `landing_url` remains authoritative for
// navigation and this helper is presentation-only.

const PRODUCTION_ROLE_LABELS = {
    owner: "Production Owner",
    admin: "Production Admin",
    contributor: "Production Contributor",
    viewer: "Production Viewer",
    producer: "Producer",
    script_supervisor: "Script Supervisor",
    owner_override: "Production Owner",
};

const DEPT_ROLE_LABELS = {
    admin: "Department Admin",
    contributor: "Contributor",
    viewer: "Viewer",
    owner_override: "Production Owner",
};

export function getWorkspaceDestination(w) {
    if (!w || typeof w !== "object") {
        return { destination: "", permission: "", testKey: "unknown" };
    }
    const isProduction = w.is_hub === true || w.dept_key == null;
    const destination = isProduction
        ? "Production"
        : (w.dept_label || w.dept_key || "");
    const rawRole = (w.dept_role || "").toLowerCase();
    const table = isProduction ? PRODUCTION_ROLE_LABELS : DEPT_ROLE_LABELS;
    const permission = table[rawRole] || "";
    const testKey = isProduction ? "production" : String(w.dept_key || "");
    return { destination, permission, testKey };
}


// Iter-158.13.2.20e.15 — Workspace card subtitle formatter.
//
// Extracted from `MyProjects.jsx` for locking behaviour in unit
// tests and to remove the retired "Walkabout" vocabulary. The
// subtitle rules are:
//   * production_name is present AND differs from project_name →
//     `${production_name} · ${destination}`
//   * production_name matches project_name (typical wizard shape)
//     OR production_name is absent → `${destination}` alone
// The Production identity is never doubled with the primary title,
// and the retired "Walkabout" literal never appears anywhere.

export function computeWorkspaceCardSubtitle(w) {
    if (!w || typeof w !== "object") return "";
    const { destination } = getWorkspaceDestination(w);
    const prod = (w.production_name || "").trim();
    const proj = (w.project_name || "").trim();
    if (prod && prod !== proj) {
        return `${prod} · ${destination}`;
    }
    return destination;
}
